import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
} from 'react'
import i18next, { TOptions } from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'
import { Options } from './utils/config'
import { languageDetectorPlugin } from './utils/language-detector'
import configCacheRaw from './cache/config.json'
import mapFeatureFlags from './utils/map-feature-flags'
import { loggerPlugin } from './utils/i18n-logger-plugin'
import { featureFlagFunc } from './utils/feature-flag-func'
import { ConfigCacheRaw, FeatureFlagFunc } from './types'
import { GetConfigQuery } from '../../graphql/cms-types'
import { logger, LogContext } from '../../helpers/logger'

i18next.use(languageDetectorPlugin).use(loggerPlugin).use(initReactI18next)

if (!i18next.isInitialized) {
  void i18next.init(Options, error => {
    if (error) logger.error(LogContext.I18N, 'i18next failed to initialise', error)
  })
}

export const I18nContext = i18next

export type TranslateFunc = (
  key: string | string[],
  options?: TOptions | string,
) => string

type CmsContext = {
  translate: TranslateFunc
  featureFlag: FeatureFlagFunc
  cacheTimestamp: string
}

const InternalizationContext = createContext<GetConfigQuery & CmsContext>(
  null!,
)

const InternalizationProvider = ({ children }: PropsWithChildren) => {
  const [configCache] = useState<ConfigCacheRaw>(
    configCacheRaw as ConfigCacheRaw,
  )

  const { t: translate } = useTranslation()

  const featureFlag = featureFlagFunc(
    mapFeatureFlags(configCache?.data?.featureFlags),
    translate as TranslateFunc,
  )

  const cacheTimestamp = new Date(configCache.ts)

  return (
    <InternalizationContext.Provider
      value={{
        translate: translate as TranslateFunc,
        featureFlag,
        cacheTimestamp: cacheTimestamp.toISOString(),
        ...configCache.data,
      }}>
      {children}
    </InternalizationContext.Provider>
  )
}

const useInternalization = () => {
  const context = useContext(InternalizationContext)
  if (context === undefined) {
    throw new Error(
      'useInternalization must be used within a InternalizationProvider',
    )
  }
  return context
}

export { InternalizationProvider, useInternalization }
